import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Pill, FlaskConical, CheckCircle, Heart, Baby, Bone, Sparkles, Stethoscope, Users, ExternalLink, CreditCard, Star, ChevronLeft, ChevronRight, Activity, Home as HomeIcon, FlaskRound, Send, Loader2, Plus, ShoppingCart, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ═══ AI Smart Test Finder (Home) ═══ */
const HomeAIFinder = () => {
  const { cartItems, addToCart, removeFromCart } = useCart();
  const [symptoms, setSymptoms] = useState('');
  const [result, setResult] = useState('');
  const [suggestedTests, setSuggestedTests] = useState([]);
  const [loading, setLoading] = useState(false);
  const resultRef = useRef(null);

  const isInCart = (name) => cartItems.some(item => (item.test_name || item.name) === name);

  const handleRecommend = async () => {
    if (!symptoms.trim()) { toast.error('Please describe your symptoms'); return; }
    setLoading(true);
    setResult('');
    setSuggestedTests([]);
    try {
      const { data } = await axios.post(`${API}/ai/recommend-tests`, { symptoms });
      setResult(data.recommendation || '');
      // Fetch matching tests from catalog for add-to-cart
      const testsRes = await axios.get(`${API}/lab-tests`);
      const profilesRes = await axios.get(`${API}/health-profiles`);
      const allItems = [...testsRes.data, ...profilesRes.data.map(p => ({ ...p, test_code: p.profile_id, name: p.name, mrp: p.selling_price }))];
      // Extract test names from AI response and match
      const recText = (data.recommendation || '').toLowerCase();
      const matched = allItems.filter(t => recText.includes(t.name?.toLowerCase()));
      setSuggestedTests(matched.slice(0, 8));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get recommendations');
    }
    setLoading(false);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 300);
  };

  const handleAddToCart = (test) => {
    const price = test.offer_price || test.mrp || test.selling_price || 0;
    addToCart({ ...test, test_name: test.name, price, category: test.category });
    toast.success(`${test.name} added to cart`);
  };

  const formatMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/### \*\*(.*?)\*\*/g, '<h3 class="font-bold text-[#0A2540] text-base mt-4 mb-2">$1</h3>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-[#0A2540]">$1</strong>')
      .replace(/\*(.*?)\*/g, '<em class="text-[#475569]">$1</em>')
      .replace(/^- /gm, '&bull; ')
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <section className="py-10 px-4 bg-gradient-to-b from-[#F8FAFC] to-white" data-testid="home-ai-finder">
      <div className="max-w-3xl mx-auto text-center mb-6">
        <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider mb-3">
          <Sparkles className="h-3.5 w-3.5" /> AI-Powered
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-[#0A2540] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Not sure which test you need?
        </h2>
        <p className="text-[#475569] text-sm sm:text-base">
          Describe your symptoms and our AI will recommend the right diagnostic tests for you
        </p>
      </div>

      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg border border-[#E2E8F0] p-5">
          <div className="flex gap-2">
            <Input
              value={symptoms}
              onChange={e => setSymptoms(e.target.value)}
              placeholder="e.g. frequent headaches, fatigue, joint pain..."
              className="h-12 rounded-xl flex-1 text-sm"
              onKeyDown={e => e.key === 'Enter' && handleRecommend()}
              data-testid="home-ai-input"
            />
            <Button onClick={handleRecommend} disabled={loading}
              className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 rounded-xl h-12 px-6"
              data-testid="home-ai-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Find Tests</>}
            </Button>
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 mt-6 text-sm text-violet-600">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span>Analyzing your symptoms...</span>
            </div>
          )}

          {result && (
            <div ref={resultRef} className="mt-5 space-y-4">
              {/* Suggested Tests with Add to Cart */}
              {suggestedTests.length > 0 && (
                <div className="bg-gradient-to-r from-violet-50 to-indigo-50 rounded-xl p-4" data-testid="ai-suggested-tests">
                  <p className="text-xs font-bold text-violet-700 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                    <ShoppingCart className="h-3.5 w-3.5" /> Quick Book Recommended Tests
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {suggestedTests.map(t => {
                      const inCart = isInCart(t.name);
                      const price = t.offer_price || t.mrp || t.selling_price || 0;
                      return (
                        <div key={t.test_code || t.profile_id} className="bg-white rounded-lg p-3 flex items-center justify-between gap-2" data-testid={`suggested-${t.test_code || t.profile_id}`}>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-[#0A2540] truncate">{t.name}</p>
                            <p className="text-xs text-[#94A3B8]">{t.category}</p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="font-bold text-sm text-[#0A2540]">Rs.{price}</span>
                            <button onClick={() => inCart ? removeFromCart(t.name) : handleAddToCart(t)}
                              className={`p-1.5 rounded-lg transition-colors ${inCart ? 'bg-green-100 text-green-600' : 'bg-violet-100 text-violet-600 hover:bg-violet-200'}`}
                              data-testid={`add-suggested-${t.test_code || t.profile_id}`}>
                              {inCart ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {cartItems.length > 0 && (
                    <Link to="/lab-tests" className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-violet-700 hover:text-violet-900" data-testid="go-to-cart-link">
                      <ShoppingCart className="h-3.5 w-3.5" /> Go to cart ({cartItems.length} items) <ArrowRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              )}

              {/* AI Response */}
              <div className="bg-[#F8FAFC] rounded-xl p-4 border border-[#E2E8F0]" data-testid="home-ai-result">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-violet-600" />
                  <span className="text-xs font-bold text-violet-600 uppercase tracking-wide">AI Analysis</span>
                </div>
                <div className="prose prose-sm max-w-none text-sm text-[#334155] leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(result) }} />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const BANNERS = [
  {
    image: 'https://customer-assets.emergentagent.com/job_amc-care/artifacts/u8yyblze_1000577584.jpg',
    alt: 'AMC Health Card - Family Health Plan starting Rs.200/month',
    link: '/health-card',
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_amc-care/artifacts/obht9e5a_1000577582.jpg',
    alt: 'Diagnostic Lab - Right Treatment Begins with Right Diagnosis',
    link: '/lab-tests',
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_amc-care/artifacts/fj2tye6g_1000577586.jpg',
    alt: 'Ultrasound Scan - Quick, Safe, Accurate',
    link: '/services',
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_amc-care/artifacts/afm0359p_1000577583.jpg',
    alt: 'Corporate Health - Caring for your team every step of the way',
    link: '/corporate',
  },
  {
    image: 'https://customer-assets.emergentagent.com/job_amc-care/artifacts/mxhzat4e_1000577585.jpg',
    alt: 'Professional Doctor Specialist - Gynecologist, Orthopedics, Dermatologist',
    link: '/specialties',
  },
];

const REVIEWS = [
  { name: 'Priya S.', rating: 5, text: 'Excellent medical center with caring staff. The doctors are very experienced and take time to explain everything. Highly recommend for families!', time: '2 months ago' },
  { name: 'Rajesh K.', rating: 5, text: 'Very good facility. Lab reports are accurate and delivered on time. The health card plan is a great value for the entire family.', time: '1 month ago' },
  { name: 'Lakshmi R.', rating: 5, text: 'Best clinic in the area. Clean, organized, and the reception staff is very helpful. Night consultation availability is a big plus!', time: '3 weeks ago' },
  { name: 'Suresh M.', rating: 4, text: 'Good experience overall. The pharmacy is well-stocked and the scan facility is modern. Dr. Priyakanth is very knowledgeable.', time: '1 month ago' },
  { name: 'Anitha D.', rating: 5, text: 'We have been visiting AMC for years. The home collection service for lab tests is very convenient. Staff is always polite and professional.', time: '2 weeks ago' },
  { name: 'Karthik V.', rating: 5, text: 'Affordable and quality healthcare. The corporate health package for our company was well organized. Great value for money.', time: '3 months ago' },
];

const BannerCarousel = () => {
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % BANNERS.length);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, []);

  const go = (dir) => {
    clearInterval(timerRef.current);
    setCurrent(prev => (prev + dir + BANNERS.length) % BANNERS.length);
    timerRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % BANNERS.length);
    }, 4000);
  };

  const b = BANNERS[current];
  return (
    <section className="relative overflow-hidden" data-testid="banner-carousel">
      <Link to={b.link} className="block">
        <img
          src={b.image}
          alt={b.alt}
          className="w-full h-auto transition-opacity duration-500"
        />
      </Link>
      {/* Navigation Arrows */}
      <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 rounded-full p-2 text-white transition-colors z-10" data-testid="banner-prev"><ChevronLeft className="h-5 w-5" /></button>
      <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 rounded-full p-2 text-white transition-colors z-10" data-testid="banner-next"><ChevronRight className="h-5 w-5" /></button>
      {/* Dots */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {BANNERS.map((_, i) => (
          <button key={i} onClick={() => { clearInterval(timerRef.current); setCurrent(i); timerRef.current = setInterval(() => setCurrent(prev => (prev + 1) % BANNERS.length), 4000); }}
            className={`h-2 rounded-full transition-all ${i === current ? 'w-8 bg-white' : 'w-3 bg-white/50'}`}
            data-testid={`banner-dot-${i}`}
          />
        ))}
      </div>
    </section>
  );
};

const Home = () => {
  const quickActions = [
    {
      icon: Phone,
      title: '24/7 Emergency',
      description: 'Round-the-clock emergency medical services',
      color: 'bg-red-50',
      iconColor: 'text-[#EF4444]',
      link: 'tel:7010200869',
    },
    {
      icon: Pill,
      title: 'Online Pharmacy',
      description: 'Order medicines online with prescription',
      color: 'bg-blue-50',
      iconColor: 'text-[#0EA5E9]',
      link: '/pharmacy',
    },
    {
      icon: FlaskConical,
      title: 'Home Lab Collection',
      description: 'Book lab tests with home sample collection',
      color: 'bg-green-50',
      iconColor: 'text-[#10B981]',
      link: '/lab-tests',
    },
  ];

  const specialties = [
    { icon: Heart, name: 'Infertility', color: 'text-pink-500' },
    { icon: Baby, name: 'Pregnancy Care', color: 'text-purple-500' },
    { icon: Bone, name: 'Orthopedics', color: 'text-orange-500' },
    { icon: Sparkles, name: 'Dermatology', color: 'text-yellow-500' },
    { icon: Stethoscope, name: 'General Medicine', color: 'text-blue-500' },
    { icon: Users, name: 'Pediatrics', color: 'text-green-500' },
  ];

  const stats = [
    { value: '10,000+', label: 'Patients Cared', icon: Users },
    { value: '7+', label: 'Diagnostic Facilities', icon: Activity },
    { value: '5,000+', label: 'Home Collections Done', icon: HomeIcon },
  ];

  return (
    <div className="min-h-screen">
      <section
        className="relative h-[600px] flex items-center justify-center"
        style={{
          backgroundImage:
            'url(https://static.prod-images.emergentagent.com/jobs/8194830b-3733-4683-8455-807cbf80703e/images/e6d825010277abf7d9f154e2f6fccb1f1bb30026015d55d05a73fdad95ca4357.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white/60 to-white/40 backdrop-blur-sm" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A2540] mb-6 tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="hero-heading"
          >
            AMC for Your Health
          </h1>
          <p className="text-lg sm:text-xl text-[#475569] mb-8 max-w-2xl mx-auto leading-relaxed">
            Unifying all your healthcare needs under one center. 24/7 medical services, diagnostics, pharmacy &amp; more — online and offline.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" data-testid="book-appointment-hero-button">
              <Button className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-full px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-2xl transition-all hover:scale-105">
                Book Appointment
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/health-card" data-testid="join-health-card-hero-button">
              <Button variant="outline" className="border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 rounded-full px-8 py-6 text-lg font-semibold">
                <CreditCard className="mr-2 h-5 w-5" />
                AMC Health Card
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Banner Carousel */}
      <BannerCarousel />

      {/* AI Smart Test Finder */}
      <HomeAIFinder />

      {/* Stats Counter */}
      <section className="py-10 px-4 bg-white" data-testid="stats-section">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-3 gap-4 sm:gap-8">
            {stats.map(s => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="text-center" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-[#0EA5E9]/10 rounded-full mb-3">
                    <Icon className="h-6 w-6 text-[#0EA5E9]" />
                  </div>
                  <p className="text-2xl sm:text-4xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.value}</p>
                  <p className="text-xs sm:text-sm text-[#475569] mt-1">{s.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {quickActions.map((action) => {
              const Icon = action.icon;
              const isExternal = action.link.startsWith('tel:');
              const Component = isExternal ? 'a' : Link;
              return (
                <Component
                  key={action.title}
                  to={!isExternal ? action.link : undefined}
                  href={isExternal ? action.link : undefined}
                  className="block"
                  data-testid={`quick-action-${action.title.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div
                    className={`${action.color} border border-[#E2E8F0] rounded-2xl p-8 transition-all hover:shadow-lg hover:-translate-y-1 cursor-pointer h-full`}
                  >
                    <Icon className={`h-12 w-12 ${action.iconColor} mb-4`} />
                    <h3 className="text-xl font-semibold text-[#0A2540] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {action.title}
                    </h3>
                    <p className="text-[#475569]">{action.description}</p>
                  </div>
                </Component>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Why Choose AMC?
            </h2>
            <p className="text-[#475569] text-lg max-w-2xl mx-auto">
              One center for all your healthcare needs — accessible online and at our facility
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-[#0EA5E9]/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-[#0EA5E9]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0A2540] mb-2">24/7 Availability</h3>
              <p className="text-[#475569]">Always here when you need us, day or night</p>
            </div>
            <div className="text-center">
              <div className="bg-[#0EA5E9]/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-[#0EA5E9]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0A2540] mb-2">Unified Healthcare</h3>
              <p className="text-[#475569]">All fragmented health services brought together under one roof</p>
            </div>
            <div className="text-center">
              <div className="bg-[#0EA5E9]/10 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-10 w-10 text-[#0EA5E9]" />
              </div>
              <h3 className="text-xl font-semibold text-[#0A2540] mb-2">NABL M(EL)T Accreditation</h3>
              <p className="text-[#475569]">Operating under NABL M(EL)T program standards for lab excellence</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-gradient-to-r from-[#0A2540] to-[#0EA5E9]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4">
                <span className="text-white text-sm font-semibold">HEALTH REVOLUTION</span>
              </div>
              <h2
                className="text-3xl sm:text-4xl font-bold text-white mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                AMC Health Card
              </h2>
              <p className="text-white/90 text-lg mb-6 leading-relaxed">
                Your subscription to a healthier life. Choose from Silver or Gold plans starting at just &#8377;200/month for a family of 4. Get unlimited consultations, pharmacy discounts, and more.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/health-card" data-testid="health-card-promo-button">
                  <Button className="bg-white text-[#0A2540] hover:bg-white/90 rounded-full px-6 py-3 font-semibold">
                    Explore Plans
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <a
                  href="https://forms.gle/Kv6yDgSkAniixb3s7"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="health-card-join-home-button"
                >
                  <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-6 py-3 font-semibold">
                    Join Now
                    <ExternalLink className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <img
                src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/90qj3n4i_1000566014.png"
                alt="AMC Health Card - First Time in Chennai"
                className="w-full max-w-lg rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-16 px-4 bg-white" data-testid="reviews-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <img src="https://www.google.com/favicon.ico" alt="Google" className="h-6 w-6" />
              <span className="text-lg font-semibold text-[#0A2540]">Google Reviews</span>
            </div>
            <div className="flex items-center justify-center gap-1 mb-2">
              {[1,2,3,4,5].map(i => <Star key={i} className="h-6 w-6 fill-[#FBBC05] text-[#FBBC05]" />)}
            </div>
            <p className="text-[#475569]">Trusted by thousands of patients across Chennai</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REVIEWS.map((review) => (
              <div key={review.name} className="bg-[#F8FAFC] rounded-2xl p-6 border border-[#E2E8F0] hover:shadow-md transition-shadow" data-testid={`review-${review.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: review.rating }, (_, i) => (
                    <Star key={i} className="h-4 w-4 fill-[#FBBC05] text-[#FBBC05]" />
                  ))}
                  {Array.from({ length: 5 - review.rating }, (_, i) => (
                    <Star key={`e-${i}`} className="h-4 w-4 text-[#E2E8F0]" />
                  ))}
                </div>
                <p className="text-sm text-[#475569] mb-4 leading-relaxed">&ldquo;{review.text}&rdquo;</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#0A2540] flex items-center justify-center text-white text-xs font-bold">
                      {review.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-[#0A2540]">{review.name}</span>
                  </div>
                  <span className="text-xs text-[#94A3B8]">{review.time}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="https://g.co/kgs/AMC247" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 rounded-full px-6 py-3">
                See All Reviews on Google
                <ExternalLink className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2
              className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Our Specialties
            </h2>
            <p className="text-[#475569] text-lg">Comprehensive healthcare services under one roof</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {specialties.map((specialty) => {
              const Icon = specialty.icon;
              return (
                <Link
                  to="/specialties"
                  key={specialty.name}
                  className="bg-white rounded-2xl p-6 text-center transition-all hover:shadow-lg hover:-translate-y-1"
                  data-testid={`specialty-card-${specialty.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className={`h-10 w-10 ${specialty.color} mx-auto mb-3`} />
                  <p className="text-sm font-medium text-[#0A2540]">{specialty.name}</p>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <Link to="/specialties" data-testid="view-all-specialties-button">
              <Button
                variant="outline"
                className="border-2 border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540]/5 rounded-full px-6 py-3"
              >
                View All Specialties
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
